import React, { useEffect } from "react";
import { useQuery } from "react-query";
import useAxiosInstance from "../../../../lib/useAxiosInstance";
import { Spin, Alert, Button, Tooltip } from "antd";
import ElectionOverviewCard from "./ElectionOverviewCard";
import Favourability from "./Favourability";
import FavourabilityLegend from "./FavourabilityLegend";
import ElectionResultCard from "./ElectionResultCard";
import { ChevronUp, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ElectionResults from "./ElectionResults";
import { useTranslation } from "react-i18next";

/* Build query params exactly like TabOne */
const buildFilterParams = (filters) => ({
  constituency: filters?.constituency || undefined,
  village: filters?.village || undefined,
  booth: filters?.booth || undefined,
});

const TabTwo = ({ survey, filters, setFavourabilityColors }) => {
  const { t } = useTranslation();
  const axiosInstance = useAxiosInstance();
  const surveyId = survey?._id || survey?.id;
  const filterParams = buildFilterParams(filters);

  const [selectedParty, setSelectedParty] = React.useState("BJP");
  const [activeInnerTab, setActiveInnerTab] = React.useState("results");
  const [showElectionResults, setShowElectionResults] = React.useState(false);

  /* ---------------- FETCH ELECTION OVERVIEW ---------------- */

  const { data: parties = [] } = useQuery({
    queryKey: ["parties", surveyId],
    enabled: !!surveyId,
    refetchOnWindowFocus: false,
    retry: 1,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/api/electionData/parties/${surveyId}`
      );
      return res?.data?.data || [];
    },
  });

  const isStateSurvey = survey?.surveyType === "state";

  const {
  data: overviewData,
  isLoading,
  isError,
  error,
} = useQuery({
  refetchOnWindowFocus: false,
  retry: 1,
  enabled: !!surveyId,
  queryKey: [
    "electionOverview",
    isStateSurvey ? "state" : "normal",
    surveyId,
    filterParams,
  ],
  queryFn: async () => {
    const url = isStateSurvey
      ? "/api/electionData/overviewState"
      : "/api/electionData/overview";

    const res = await axiosInstance.get(url, {
      params: {
        surveyId,
        ...(!isStateSurvey ? filterParams : {}), 
      },
    });

    return res?.data?.data || null;
  },
});

  const {
  data: favourabilityData,
  isLoading: favourabilityLoading,
} = useQuery({
  refetchOnWindowFocus: false,
  retry: 1,
  enabled: !!surveyId && !!selectedParty,
  queryKey: [
    "favourability",
    surveyId,
    selectedParty,
    survey?.surveyType,
  ],
  queryFn: async () => {
    let url;

    if (survey?.surveyType === "parliamentary") {
      url = "/api/electionData/favourabilityParliamentary";
    } else if (survey?.surveyType === "state") {
      url = "/api/electionData/favourabilityState";
    }else {
      url = "/api/electionData/favourability";
    }

    const res = await axiosInstance.get(url, {
      params: {
        surveyId,
        party: selectedParty,
      },
    });

    return res?.data?.data || [];
  },
});

const {
  data: electionResultData,
  isLoading: electionResultLoading,
  isError: electionResultError,
} = useQuery({
  refetchOnWindowFocus: false,
  retry: 1,
  enabled: !!surveyId,
  queryKey: ["electionResult", surveyId, filterParams, survey?.surveyType],
  queryFn: async () => {
    let url = "";
    let params = { };

    // =========================
    // ASSEMBLY SURVEY
    // =========================
    if (survey?.surveyType === "assembly") {
      // Always constituency level, NO filters
      url = `/api/electionData/electionResultConstituency/${surveyId}`;
      // do NOT add constituency/village/booth
    }

    // =========================
    // PARLIAMENTARY SURVEY
    // =========================
    else if (survey?.surveyType === "parliamentary") {
      url = `/api/electionData/electionResultParliamentary/${surveyId}`;
    }

    else if (survey?.surveyType === "state") {
      url = `/api/electionData/electionResultState/${surveyId}`;
    }
      // Booth selected (always implies village)
      if (filterParams.village && filterParams.booth) {
        url = `/api/electionData/electionResultBooth/${surveyId}`;
        params.booth = filterParams.booth;
        params.village = filterParams.village;
      }
      // Village selected (no booth)
      else if (filterParams.village) {
        url = `/api/electionData/electionResultVillage/${surveyId}`;
        params.village = filterParams.village;
      }
      // Default → constituency
      else if(filterParams.constituency && survey.surveyType!=='state') {
        url = `/api/electionData/electionResultConstituency/${surveyId}`;
        params.constituency = filterParams.constituency;
      }
      else if(filterParams.constituency && survey.surveyType==='state') {
        url = `/api/electionData/electionResultConstituencyForState/${surveyId}`;
        params.constituency = filterParams.constituency.toUpperCase();
      }

    const res = await axiosInstance.get(url, { params });
    return res?.data?.data || [];
  },
});

const selectedPartyResult = React.useMemo(() => {
  if (!electionResultData || !selectedParty) return null;
  return electionResultData.find(
    (r) => r.party === selectedParty
  );
}, [electionResultData, selectedParty]);

useEffect(() => {
  if (!favourabilityData || !Array.isArray(favourabilityData)) return;

  const colors = favourabilityData.map((item) => {
    // Assembly → village-level
    if (survey?.surveyType === "assembly") {
      return {
        village: item.Village,
        color: item.color,
        category: item.category,
        party: selectedParty,
      };
    }

    // Parliamentary + State → constituency-level
    return {
      constituency: item.constituency,
      color: item.color,
      category: item.category,
      party: selectedParty,
    };
  });

  setFavourabilityColors(colors);
}, [
  selectedParty,
  favourabilityData,
  survey?.surveyType,
  setFavourabilityColors,
]);


  if (isLoading || electionResultLoading) {
    return (
      <div className="flex justify-center items-center h-40">
        <Spin size="large" />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert
        type="error"
        message="Failed to load election overview"
        description={error?.message || "Something went wrong"}
        showIcon
      />
    );
  }

  if (!overviewData) {
    return (
      <Alert
        type="info"
        message="No data available"
        description="Election overview data not found for selected filters."
        showIcon
      />
    );
  }

  return (
    <div className="p-4 space-y-4">

      <div className="flex gap-3 mb-3 absolute top-[20%] right-2 bg-white/80 rounded-full p-1 border border-gray-200 z-[20] p-2 w-[320px]">
        <button
          onClick={() => setActiveInnerTab("results")}
          className={`flex-1 p-1.5 text-sm rounded-full font-medium flex items-center justify-center gap-1 transition-all ${
            activeInnerTab === "results"
              ? "bg-blue-600 text-white shadow-sm"
              : "text-gray-700 hover:bg-gray-100"
          }`}
        >
          {t("electionResult")}
        </button>

        <button
          onClick={() => setActiveInnerTab("favourability")}
          className={`flex-1 p-1.5 text-sm rounded-full font-medium flex items-center justify-center gap-1 transition-all ${
            activeInnerTab === "favourability"
              ? "bg-blue-600 text-white shadow-sm"
              : "text-gray-700 hover:bg-gray-100"
          }`}
        >
          {t("favourability")}
        </button>
      </div>

      <div className="absolute top-[10%] right-2 z-[20]">
        <select
          className="rounded-full border px-2 py-1 text-s card-wrapper text-black w-[320px] text-center"
          value={selectedParty}
          onChange={(e) => setSelectedParty(e.target.value)}
        >
          {parties.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      {/* ELECTION RESULTS TOGGLE */}
      {electionResultData?.length > 0 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-[30]">
          <Tooltip title={showElectionResults ? "Hide Election Results" : "View Election Results"}>
            <Button
              type="primary"
              shape="circle"
              size="large"
              icon={showElectionResults ? <ChevronDown size={22} /> : <ChevronUp size={22} />}
              className="shadow-xl bg-[#0b65c2] hover:bg-[#123865] transition-transform hover:scale-110 flex justify-center"
              onClick={() => setShowElectionResults(!showElectionResults)}
            />
          </Tooltip>
        </div>
      )}
      <AnimatePresence>
      {showElectionResults && (
        <ElectionResults
          surveyType={survey?.surveyType}
          data={electionResultData}
          onClose={() => setShowElectionResults(false)}
        />
      )}
    </AnimatePresence>

    <ElectionOverviewCard overview={overviewData} />

    {activeInnerTab === "results" && selectedPartyResult && (
      <ElectionResultCard
        surveyType={survey?.surveyType}
        result={selectedPartyResult}
        party={selectedParty}
      />
    )}

    {activeInnerTab === "favourability" && (
      <Favourability
        data={favourabilityData}
        loading={favourabilityLoading}
        party={selectedParty}
        // onPartyChange={setSelectedParty}
        surveyId={surveyId}
      />
    )}
    
    <FavourabilityLegend />
    
    </div>
  );
};

/* ---------------- SMALL REUSABLE CARD ---------------- */

const OverviewCard = ({ label, value }) => (
  <div className="bg-white rounded-xl shadow p-4 flex flex-col items-center z-[999]">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-xl font-bold text-[#0b65c2]">{value}</span>
  </div>
);

export default TabTwo;
